/**
 * ValueRepository - Database operations for values collection
 */

import { BaseRepository } from './BaseRepository';
import { where } from 'firebase/firestore';

export class ValueRepository extends BaseRepository {
  constructor() {
    // CRITICAL: Enable hierarchical paths - values are now stored at schools/{schoolId}/values/{valueId}
    super('values', { isHierarchical: true });
  }

  /**
   * Get all active values sorted by sortOrder then name
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Array of active values
   */
  async getActiveValues(schoolId) {
    if (!schoolId) {
      console.warn('ValueRepository.getActiveValues: schoolId required but not provided');
      return [];
    }

    console.log('ValueRepository.getActiveValues() called');
    // Avoid complex query with index requirement - filter and sort client-side
    const constraints = [
      where('active', '==', true)
    ];

    const results = await this.queryDocuments(constraints, schoolId);
    console.log('ValueRepository.getActiveValues() results before sort:', results);

    // Sort client-side by sortOrder (descending - highest first), then by name
    const sorted = results.sort((a, b) => {
      const orderA = a.sortOrder ?? 0;
      const orderB = b.sortOrder ?? 0;
      if (orderA !== orderB) return orderB - orderA; // DESC: higher numbers first
      return (a.name || '').localeCompare(b.name || '');
    });
    console.log('ValueRepository.getActiveValues() results after sort:', sorted);
    return sorted;
  }

  /**
   * Get all values (including inactive)
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Array of all values
   */
  async getAllValues(schoolId) {
    if (!schoolId) {
      console.warn('ValueRepository.getAllValues: schoolId required but not provided');
      return [];
    }

    // Get all values and sort client-side
    const results = await this.getAll(schoolId);
    return results.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }

  /**
   * Get value by name
   * @param {string} name - Value name
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object|null>} Value object or null
   */
  async getByName(name, schoolId) {
    if (!schoolId) {
      console.warn('ValueRepository.getByName: schoolId required but not provided');
      return null;
    }

    const constraints = [
      where('name', '==', name)
    ];

    const results = await this.queryDocuments(constraints, schoolId);
    return results.length > 0 ? results[0] : null;
  }

  /**
   * Create new value
   * @param {Object} valueData - Value data
   * @returns {Promise<Object>} Created value
   */
  async createValue(valueData) {
    console.log('ValueRepository.createValue() called with:', valueData);
    // Don't pass timestamps - BaseRepository handles them
    const result = await this.create({
      ...valueData,
      active: valueData.active !== false, // Default to active
      sortOrder: valueData.sortOrder !== undefined ? valueData.sortOrder : 0 // Default sort order
    });
    console.log('ValueRepository.createValue() result:', result);
    return result;
  }

  /**
   * Update value
   * @param {string} valueId - Value ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<Object>} Updated value
   */
  async updateValue(valueId, updates) {
    return this.update(valueId, updates);
  }

  /**
   * Deactivate value (soft delete)
   * @param {string} valueId - Value ID
   * @returns {Promise<void>}
   */
  async deactivateValue(valueId, schoolId) {
    return this.update(valueId, {
      active: false, schoolId
    });
  }

  /**
   * Hard delete value
   * @param {string} valueId - Value ID
   * @returns {Promise<void>}
   */
  async deleteValue(valueId) {
    return this.delete(valueId);
  }

  /**
   * Search values by name
   * @param {string} searchTerm - Search term
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Array>} Matching values
   */
  async searchValues(searchTerm, schoolId) {
    const allValues = await this.getAllValues(schoolId);
    const lowerSearch = searchTerm.toLowerCase();

    return allValues.filter(value =>
      value.name?.toLowerCase().includes(lowerSearch) ||
      value.description?.toLowerCase().includes(lowerSearch)
    );
  }

  /**
   * Get value statistics
   * @param {string} schoolId - School ID (required for hierarchical collections)
   * @returns {Promise<Object>} Statistics
   */
  async getValueStatistics(schoolId) {
    const allValues = await this.getAllValues(schoolId);
    const activeValues = allValues.filter(v => v.active !== false);

    return {
      totalValues: allValues.length,
      activeValues: activeValues.length,
      inactiveValues: allValues.length - activeValues.length
    };
  }

  /**
   * Update sort orders for multiple values
   * @param {Array} updates - Array of {id, sortOrder}
   * @returns {Promise<void>}
   */
  async updateValueSortOrders(updates, schoolId) {
    const promises = updates.map(({ id, sortOrder }) =>
      this.update(id, { sortOrder, schoolId })
    );
    await Promise.all(promises);
  }
}
